import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import useMember from "@/hooks/useMember";
import EmailGateModal from "@/components/feature/EmailGateModal";
import { logActivity } from "@/lib/activityLog";
import { normalizeButtonPosition, OVERLAY_POSITION_CLASSES, isBelowPosition } from "@/lib/bannerPosition";

interface ContactInfo {
  name?: string;
  whatsapp?: string;
  email?: string;
}

interface Banner {
  id: number;
  title: string;
  subtitle: string | null;
  button_text: string | null;
  button_link: string;
  button_position?: string | null;
  image_url: string;
  sort_order: number;
  category?: string;
  template_id?: string | null;
  contact_info: ContactInfo | null;
  member_id?: number | null;
}

function getCardsPerView() {
  if (typeof window === "undefined") return 3;
  const w = window.innerWidth;
  if (w < 640) return 1;
  if (w < 1024) return 2;
  return 3;
}

const SWIPE_THRESHOLD = 50;
const AUTOPLAY_INTERVAL = 8000;

export default function BannerCarousel() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(getCardsPerView());
  const [noTransition, setNoTransition] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const trackRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { isAuthenticated } = useAuth();
  const { member } = useMember();

  const [showEmailGate, setShowEmailGate] = useState(false);
  const [interestSending, setInterestSending] = useState<number | null>(null);
  const [interestSent, setInterestSent] = useState<Set<number>>(new Set());
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const [memberByIdMap, setMemberByIdMap] = useState<
    Record<string, { name: string; photo: string | null }>
  >({});

  const totalSlides = banners.length;

  // Infinite carousel array: [last N] + [originals] + [first N]
  const carouselBanners = (() => {
    if (banners.length <= cardsPerView) return banners;
    const head = banners.slice(-cardsPerView);
    const tail = banners.slice(0, cardsPerView);
    return [...head, ...banners, ...tail];
  })();

  const totalPages = banners.length <= cardsPerView ? 1 : Math.ceil(banners.length / cardsPerView);

  // Load banners + member photo map
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    supabase
      .from("banners")
      .select("id, title, subtitle, button_text, button_link, image_url, sort_order, active, contact_info, created_at, category, template_id, member_id, moderation_status, button_position")
      .eq("active", true)
      .order("sort_order", { ascending: true })
      .then(async ({ data, error: err }) => {
        if (cancelled) return;
        if (err) {
          setError(true);
          setLoading(false);
        } else if (data) {
          const bannerList = data as Banner[];
          setBanners(bannerList);
          setCurrentIndex(getCardsPerView());

          // Autoria por member_id (anúncios novos)
          const bannerMemberIds = [...new Set(
            bannerList
              .filter((b) => b.member_id)
              .map((b) => b.member_id as number)
          )];

          if (bannerMemberIds.length > 0) {
            const { data: membersById, error: byIdErr } = await supabase
              .from("members_public")
              .select("id, full_name, photo")
              .in("id", bannerMemberIds);

            if (!byIdErr && membersById) {
              const byIdMap: Record<string, { name: string; photo: string | null }> = {};
              membersById.forEach((m: { id: string | number; full_name: string; photo: string | null }) => {
                byIdMap[String(m.id)] = { name: m.full_name, photo: m.photo };
              });
              if (!cancelled) setMemberByIdMap(byIdMap);
            }
          }
          if (!cancelled) setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Responsive cardsPerView
  useEffect(() => {
    const handleResize = () => {
      const newCardsPerView = getCardsPerView();
      setCardsPerView((prev) => {
        if (prev !== newCardsPerView) {
          setCurrentIndex(newCardsPerView);
        }
        return newCardsPerView;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reposition without transition (infinite carousel jump)
  const reposition = useCallback(
    (newIndex: number) => {
      const timer = setTimeout(() => {
        setNoTransition(true);
        setCurrentIndex(newIndex);
        const clearTimer = setTimeout(() => {
          setNoTransition(false);
        }, 50);
        return () => clearTimeout(clearTimer);
      }, 600);
      return () => clearTimeout(timer);
    },
    []
  );

  const goNext = useCallback(() => {
    if (banners.length <= cardsPerView) return;
    setCurrentIndex((prev) => {
      const next = prev + cardsPerView;
      if (next + cardsPerView - 1 >= carouselBanners.length) {
        reposition(cardsPerView);
        return prev;
      }
      return next;
    });
  }, [banners.length, cardsPerView, totalSlides, reposition, carouselBanners.length]);

  const goPrev = useCallback(() => {
    if (banners.length <= cardsPerView) return;
    setCurrentIndex((prev) => {
      const next = prev - cardsPerView;
      if (next < 0) {
        reposition(totalSlides);
        return prev;
      }
      return next;
    });
  }, [banners.length, cardsPerView, totalSlides, reposition]);

  // Auto-play
  const startAutoPlay = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (banners.length <= cardsPerView) return;
    intervalRef.current = setInterval(() => {
      goNext();
    }, AUTOPLAY_INTERVAL);
  }, [banners.length, cardsPerView, goNext]);

  useEffect(() => {
    if (!isPaused) {
      startAutoPlay();
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPaused, startAutoPlay]);

  // Touch / Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].screenX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      if (diff > 0) {
        goNext();
      } else {
        goPrev();
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Cleanup toast timer
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const handleBannerInterest = async (banner: Banner) => {
    if (!member || interestSending) return;
    setInterestSending(banner.id);

    const payload = {
      bannerTitle: banner.title,
      bannerSubtitle: banner.subtitle,
      bannerCategory: banner.category || "",
      advertiserName: banner.contact_info?.name || "",
      advertiserEmail: banner.contact_info?.email || "",
      advertiserWhatsapp: banner.contact_info?.whatsapp || "",
      memberName: member.full_name,
      memberEmail: member.email,
      memberWhatsapp: member.whatsapp || "",
      memberProfession: member.profession || "",
      memberCityState: member.city_state || "",
    };

    try {
      await supabase.functions.invoke("notify-banner-interest", { body: payload });
    } catch {
      // mesmo com erro na edge function, segue o fluxo de UX
    }

    setInterestSent((prev) => new Set(prev).add(banner.id));
    setToastMessage("Recebemos seu interesse! A Ana Karina entrará em contato com você em breve.");

    logActivity("banner_interest", {
      banner_id: banner.id,
      banner_title: banner.title,
      banner_category: banner.category || "",
      member_name: member.full_name,
      member_email: member.email,
    });

    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastMessage(null), 6000);

    setInterestSending(null);
  };

  const renderCtaButton = (banner: Banner) => {
    if (!banner.button_text) return null;
    return (
      <>
        {isAuthenticated && member ? (
          banner.contact_info != null && banner.contact_info.name ? (
            interestSent.has(banner.id) ? (
              <span className="font-body text-xs md:text-sm px-3 sm:px-4 py-2 rounded-full bg-white/20 text-white inline-flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-check-line text-sm" />
                Interesse enviado!
              </span>
            ) : (
              <button
                onClick={() => handleBannerInterest(banner)}
                disabled={interestSending === banner.id}
                className="font-body text-xs md:text-sm px-3 sm:px-4 py-2 rounded-full bg-white text-primary tracking-wide transition-all duration-300 hover:bg-white/90 hover:scale-[1.02] whitespace-nowrap inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
              >
                {interestSending === banner.id ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin inline-block" />
                    Enviando...
                  </>
                ) : (
                  <>
                    {banner.button_text}
                    <i className="ri-arrow-right-line text-sm" />
                  </>
                )}
              </button>
            )
          ) : (
            <Link
              to="/enviar-anuncio"
              className="font-body text-xs md:text-sm px-3 sm:px-4 py-2 rounded-full bg-white text-primary tracking-wide transition-all duration-300 hover:bg-white/90 hover:scale-[1.02] whitespace-nowrap inline-flex items-center gap-1.5"
            >
              {banner.button_text}
              <i className="ri-arrow-right-line text-sm" />
            </Link>
          )
        ) : isAuthenticated ? (
          <span className="font-body text-xs md:text-sm px-3 sm:px-4 py-2 rounded-full bg-white/20 text-white/60 tracking-wide whitespace-nowrap inline-flex items-center gap-1.5">
            Carregando...
          </span>
        ) : (
          <button
            onClick={() => setShowEmailGate(true)}
            className="font-body text-xs md:text-sm px-3 sm:px-4 py-2 rounded-full bg-white text-primary tracking-wide transition-all duration-300 hover:bg-white/90 hover:scale-[1.02] whitespace-nowrap inline-flex items-center gap-1.5 cursor-pointer"
          >
            {banner.button_text}
            <i className="ri-arrow-right-line text-sm" />
          </button>
        )}
      </>
    );
  };

  // Loading state
  if (loading) {
    return (
      <section className="w-full">
        <div className="w-full">
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        </div>
      </section>
    );
  }

  // Error or empty state
  if (error || banners.length === 0) {
    return null;
  }

  const showNav = banners.length > cardsPerView;

  // Real slide index for dots
  const realSlideIndex =
    banners.length <= cardsPerView
      ? 0
      : ((currentIndex - cardsPerView) % totalSlides + totalSlides) % totalSlides;

  const activePage = banners.length <= cardsPerView ? 0 : Math.floor(realSlideIndex / cardsPerView);

  return (
    <section
      id="anuncios"
      className="w-full"
    >
      <div className="w-full">
        <div
          className="bg-primary px-0 md:px-10 lg:px-14 py-12 md:py-16 relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Section header */}
          <div className="text-center mb-10 md:mb-12 px-4 md:px-0">
            <p className="font-body text-xs tracking-[0.25em] uppercase text-white/70 mb-3">
              Oportunidades
            </p>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-white">
              Anúncios da comunidade
            </h2>
            <p className="text-white/70 mt-4 max-w-xl mx-auto text-base leading-relaxed">
              Descubra oportunidades exclusivas compartilhadas por mulheres da
              nossa rede. Conecte-se, colabore e cresça junto com a comunidade.
            </p>
          </div>

        {/* Carousel */}
        <div className="relative">
          {/* Navigation Arrows */}
          {showNav && (
            <>
              <button
                onClick={goPrev}
                className="absolute left-3 md:left-0 top-1/2 -translate-y-1/2 md:-translate-x-5 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white text-primary hover:bg-white/90 transition-all duration-300 cursor-pointer"
                aria-label="Anterior"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <button
                onClick={goNext}
                className="absolute right-3 md:right-0 top-1/2 -translate-y-1/2 md:translate-x-5 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white text-primary hover:bg-white/90 transition-all duration-300 cursor-pointer"
                aria-label="Próximo"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </>
          )}

          {/* Cards container */}
          <div
            className="md:mx-12 select-none"
            style={{ overflowX: 'clip', overflowY: 'visible' } as React.CSSProperties}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              ref={trackRef}
              className={`flex ${noTransition ? "" : "transition-transform duration-500 ease-in-out"}`}
              style={{
                transform: `translateX(-${currentIndex * (100 / cardsPerView)}%)`,
              }}
            >
              {carouselBanners.map((banner, idx) => {
                const pos = normalizeButtonPosition(banner.button_position);
                const overlayClass = OVERLAY_POSITION_CLASSES[pos];
                const isBelow = isBelowPosition(banner.button_position);
                const ctaNode = renderCtaButton(banner);

                const authorMember = banner.member_id
                  ? memberByIdMap[String(banner.member_id)]
                  : null;
                const authorName = (authorMember?.name || banner.contact_info?.name || "").trim();
                const authorPhotoUrl = authorMember?.photo || null;
                const authorLinkId = banner.member_id ? String(banner.member_id) : null;
                const authorLabel = authorName.length > 22
                  ? authorName.split(/\s+/)[0] || authorName
                  : authorName;
                const chipConflictsWithButton = pos === "bottom-center" || pos === "bottom-left";

                const authorChipInner = (
                  <>
                    {authorPhotoUrl && (
                      <img
                        src={authorPhotoUrl}
                        alt={authorName}
                        className="w-4 h-4 rounded-full object-cover flex-shrink-0"
                      />
                    )}
                    <span className="font-body">
                      Por:{" "}
                      <span className="banner-author-chip__name font-semibold">{authorLabel}</span>
                    </span>
                  </>
                );

                const authorChip = authorName ? (
                  authorLinkId ? (
                    <Link
                      to={`/perfil/${authorLinkId}`}
                      className="banner-author-chip banner-author-chip--pulse inline-flex items-center gap-1.5 max-w-full"
                      aria-label={`Ver perfil de ${authorName}`}
                    >
                      {authorChipInner}
                    </Link>
                  ) : (
                    <span className="banner-author-chip banner-author-chip--pulse inline-flex items-center gap-1.5 max-w-full">
                      {authorChipInner}
                    </span>
                  )
                ) : null;

                return (
                <div
                  key={`${banner.id}-${idx}`}
                  className="shrink-0 px-3 md:px-3"
                  style={{ width: `${100 / cardsPerView}%` }}
                >
                  <div className="group aspect-[4/5] min-h-fit rounded-2xl transition-all duration-500 hover:shadow-md relative flex flex-col justify-end overflow-hidden">
                    {/* Image wrapper - handles overflow clipping for zoom effect and rounded corners */}
                    <div className="absolute inset-0 rounded-2xl overflow-hidden">
                      <img
                        src={banner.image_url}
                        alt={banner.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        draggable={false}
                        loading="lazy"
                      />

                      {/* Gradient overlay for text readability (only when there's text) */}
                      {(banner.template_id || banner.subtitle) && (
                        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
                      )}

                      {/* Clickable whole card when there's no CTA button */}
                      {!banner.button_text && banner.button_link &&
                        (banner.button_link.startsWith("http") ? (
                          <a
                            href={banner.button_link}
                            target="_blank"
                            rel="nofollow noopener noreferrer"
                            className="absolute inset-0 z-[5]"
                            aria-label={banner.title}
                          />
                        ) : (
                          <Link
                            to={banner.button_link}
                            className="absolute inset-0 z-[5]"
                            aria-label={banner.title}
                          />
                        ))}
                    </div>

                    {/* CTA sobreposto (posição personalizada) */}
                    {ctaNode && overlayClass && (
                      <div className={`absolute ${overlayClass} z-20`}>
                        {ctaNode}
                      </div>
                    )}

                    {/* Author chip: movido pro canto superior esquerdo quando sobreporia o CTA */}
                    {authorChip && chipConflictsWithButton && (
                      <div className="absolute top-3 left-3 z-10">{authorChip}</div>
                    )}

                    {/* Card Content - in normal flow, pushed to bottom by flex */}
                    <div className="relative z-10 p-3 sm:p-4 md:p-5 flex flex-col gap-2 sm:gap-2.5 md:gap-3.5">
                      {banner.template_id && (
                        <h3 className="font-display text-sm md:text-base text-white leading-tight line-clamp-2">
                          {banner.title}
                        </h3>
                      )}
                      {banner.subtitle && (
                        <p className="font-body text-xs md:text-sm text-white/80 leading-snug line-clamp-2">
                          {banner.subtitle}
                        </p>
                      )}

                      {/* CTA Button */}
                        {banner.button_text && !overlayClass && !isBelow && (
                          <div>
                            {isAuthenticated && member ? (
                              banner.contact_info != null && banner.contact_info.name ? (
                                interestSent.has(banner.id) ? (
                                  <span className="font-body text-xs md:text-sm px-3 sm:px-4 py-2 rounded-full bg-white/20 text-white inline-flex items-center gap-1.5 whitespace-nowrap">
                                    <i className="ri-check-line text-sm" />
                                    Interesse enviado!
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => handleBannerInterest(banner)}
                                    disabled={interestSending === banner.id}
                                    className="font-body text-xs md:text-sm px-3 sm:px-4 py-2 rounded-full bg-white text-primary tracking-wide transition-all duration-300 hover:bg-white/90 hover:scale-[1.02] whitespace-nowrap inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                                  >
                                    {interestSending === banner.id ? (
                                      <>
                                        <span className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin inline-block" />
                                        Enviando...
                                      </>
                                    ) : (
                                      <>
                                        {banner.button_text}
                                        <i className="ri-arrow-right-line text-sm" />
                                      </>
                                    )}
                                  </button>
                                )
                              ) : (
                                <Link
                                  to="/enviar-anuncio"
                                  className="font-body text-xs md:text-sm px-3 sm:px-4 py-2 rounded-full bg-white text-primary tracking-wide transition-all duration-300 hover:bg-white/90 hover:scale-[1.02] whitespace-nowrap inline-flex items-center gap-1.5"
                                >
                                  {banner.button_text}
                                  <i className="ri-arrow-right-line text-sm" />
                                </Link>
                              )
                            ) : isAuthenticated ? (
                              <span className="font-body text-xs md:text-sm px-3 sm:px-4 py-2 rounded-full bg-white/20 text-white/60 tracking-wide whitespace-nowrap inline-flex items-center gap-1.5">
                                Carregando...
                              </span>
                            ) : (
                              <button
                                onClick={() => setShowEmailGate(true)}
                                className="font-body text-xs md:text-sm px-3 sm:px-4 py-2 rounded-full bg-white text-primary tracking-wide transition-all duration-300 hover:bg-white/90 hover:scale-[1.02] whitespace-nowrap inline-flex items-center gap-1.5 cursor-pointer"
                              >
                                {banner.button_text}
                                <i className="ri-arrow-right-line text-sm" />
                              </button>
                            )}
                          </div>
                        )}

                        {/* Contact footer (author chip) */}
                        {authorChip && !chipConflictsWithButton && (
                          <div className="pt-2">{authorChip}</div>
                        )}
                    </div>
                  </div>

                  {ctaNode && isBelow && (
                    <div className="pt-2">{ctaNode}</div>
                  )}
                </div>
                );
              })}
            </div>
          </div>

          {/* Dot indicators */}
          {showNav && (
            <div className="flex items-center justify-center gap-2 mt-8">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setCurrentIndex(i * cardsPerView + cardsPerView);
                  }}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${
                    i === activePage
                      ? "w-5 h-2 bg-white"
                      : "w-2 h-2 bg-white/40 hover:bg-white/60"
                  }`}
                  aria-label={`Página ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Email Gate Modal */}
      <EmailGateModal isOpen={showEmailGate} onClose={() => setShowEmailGate(false)} />

      {/* Toast de feedback */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm bg-white/95 backdrop-blur-sm text-brown px-5 py-4 rounded-lg border border-beige/50">
          <div className="flex items-start gap-3">
            <i className="ri-checkbox-circle-fill text-green-600 text-xl flex-shrink-0 mt-0.5" />
            <div className="relative pr-5">
              <p className="font-body text-sm leading-relaxed">{toastMessage}</p>
              <button
                onClick={() => setToastMessage(null)}
                className="absolute top-0 right-0 text-brown/40 hover:text-brown/70 cursor-pointer"
                aria-label="Fechar"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}